import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AboutPage = () => {
  const certifications = [
    { title: 'PADI Master Scuba Diver Trainer (MSDT)', year: '2016', icon: '🤿' },
    { title: 'First Aid & CPR Certified', year: '2024', icon: '🩹' },
    { title: 'Mechanical Engineering Degree', year: '2014', icon: '⚙️' },
    { title: 'Wilderness Survival Training', year: '2018', icon: '🏕️' }
  ];

  const stats = [
    { number: '8+', label: 'Years Experience', icon: '📅' },
    { number: '500+', label: 'Happy Adventurers', icon: '👥' },
    { number: '100%', label: 'Safety Record', icon: '🛡️' },
    { number: '4', label: 'Max Group Size', icon: '🤝' }
  ];

  const philosophy = [
    {
      title: 'Mindful Connection',
      description: 'Every adventure is designed to foster a deeper connection with nature, yourself, and fellow adventurers.',
      icon: '🧘'
    },
    {
      title: 'Safety First',
      description: 'Professional training, quality equipment, and careful planning ensure every experience is both thrilling and safe.',
      icon: '🛡️'
    },
    {
      title: 'Small Groups',
      description: 'Limited to 4 people maximum, ensuring personalized attention and meaningful connections.',
      icon: '👥'
    },
    {
      title: 'Ocean Conservation',
      description: 'Promoting respect for marine environments and sustainable practices in all our activities.',
      icon: '🌊'
    }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="relative h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_booking-integration/artifacts/nbb89zpg_1000054816.jpg)`,
            backgroundPosition: 'center 40%'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 h-full flex items-center justify-center text-center">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">Meet Ahmed Elsayed</h1>
              <p className="text-xl text-white/90 mb-6">
                MSDT Certified Instructor • Ocean Conservationist • Adventure Guide
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 sm:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-4 sm:p-5 bg-gradient-to-b from-coral-50 to-orange-50 rounded-xl"
              >
                <div className="text-2xl sm:text-3xl mb-2">{stat.icon}</div>
                <div className="text-2xl sm:text-3xl font-bold text-coral-600 mb-1">{stat.number}</div>
                <div className="text-slate-600 font-medium text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Story */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-coral-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Introduction */}
          <div className="text-center mb-10 sm:mb-12 bg-gradient-to-r from-coral-25 to-blue-25 rounded-xl p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">My Journey to the Ocean</h2>
              <p className="text-lg sm:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
                Meet Ahmed, a passionate diver and instructor whose bond with the ocean runs deep—rooted in childhood wonder, shaped by life-changing encounters, and driven by a calling to connect others with the sea.
              </p>
            </motion.div>
          </div>

          {/* Timeline Journey */}
          <div className="space-y-16">
            
            {/* Early Years Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            >
              <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                <div className="absolute -left-4 top-8 w-1 h-3/4 bg-gradient-to-b from-coral-500 to-blue-500 rounded-full"></div>
                <div className="absolute -left-7 top-12 w-7 h-7 bg-coral-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-sm font-bold">7</span>
                </div>
                <div className="pl-4">
                  <h3 className="text-3xl font-bold text-slate-800 mb-4 flex items-center">
                    <span className="mr-3 text-4xl">🌊</span>
                    First Breath of Ocean Magic
                  </h3>
                  <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                    <p>
                      Ahmed's journey began in the Red Sea at just seven years old, when his father first handed him a snorkel. He was instantly spellbound by the explosion of life beneath the surface—vibrant corals, shimmering fish in every shape and color, from slender needlefish dancing near the surface to giant groupers resting on the reef floor. That early moment of awe sparked a lifelong love for the underwater world.
                    </p>
                    <p>
                      By ten, he was free diving with nothing but a mask and fins. At twelve, he took his first breath from a scuba regulator on a tourist boat in Sharm El Sheikh. The memory is still vivid: floating eye-to-eye with the fish, completely weightless and finally feeling connected to the ocean in a way he couldn't explain. His instructor trusted him instantly, letting him move freely—and from that moment, he knew this was his path.
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_booking-integration/artifacts/waw1quej_1000054815.jpg"
                    alt="Ahmed diving in the Red Sea - his childhood diving waters"
                    className="w-full h-80 sm:h-96 object-cover brightness-110 contrast-110"
                    style={{ objectPosition: 'center 40%' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10"></div>
                </div>
                <div className="absolute top-6 right-6 bg-coral-500/95 backdrop-blur-sm text-white px-6 py-3 rounded-2xl shadow-xl">
                  <span className="font-semibold text-lg">Red Sea, Egypt</span>
                </div>
              </div>
            </motion.div>

            {/* Dahab Training Section */}
            <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-3xl p-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
              <div className="order-2 lg:order-1 relative">
                <div className="relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_booking-integration/artifacts/hrh9v168_GOPR6914.jpg"
                    alt="Dahab diving training - Mezo and Olesh"
                    className="w-full h-96 object-cover brightness-110 contrast-105"
                    style={{ objectPosition: 'center 30%' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
                </div>
                <div className="absolute bottom-6 left-6 bg-blue-500/95 backdrop-blur-sm text-white px-6 py-3 rounded-2xl shadow-xl">
                  <span className="font-semibold text-lg">Dahab, Egypt</span>
                </div>
              </div>
              <div className="order-1 lg:order-2 relative bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                <div className="absolute -right-4 top-8 w-1 h-3/4 bg-gradient-to-b from-blue-500 to-teal-500 rounded-full"></div>
                <div className="absolute -right-7 top-12 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-xs font-bold">DM</span>
                </div>
                <div className="pr-4">
                  <h3 className="text-3xl font-bold text-slate-800 mb-4 flex items-center">
                    <span className="mr-3 text-4xl">🎯</span>
                    Legendary Dahab Training
                  </h3>
                  <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                    <p>
                      Years later, Ahmed trained in the legendary waters of Dahab, Egypt, where he earned his Divemaster certification under the guidance of Mezo and Olesh—two brothers who didn't just teach him how to dive, but how to live. "Mezo didn't just shape me into a Divemaster," Ahmed says, "he changed my life." Despite his restless spirit and love of travel, Dahab taught him discipline, presence, and the sacred rhythm of life underwater.
                    </p>
                    <p>
                      His most unforgettable dive happened on his final trip to Dahab, during an emotional farewell dive in the Canyon. As he ascended from 24 meters, heart heavy with goodbyes, a majestic manta ray glided by at the canyon's exit—as if the ocean itself was saying "see you soon."
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            </div>

            {/* Australia & MSDT Section */}
            <div className="bg-gradient-to-l from-teal-50 to-green-50 rounded-3xl p-8 mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
              <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                <div className="absolute -left-4 top-8 w-1 h-3/4 bg-gradient-to-b from-teal-500 to-green-500 rounded-full"></div>
                <div className="absolute -left-8 top-12 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-xs font-bold">AU</span>
                </div>
                <div className="pl-8">
                  <h3 className="text-3xl font-bold text-slate-800 mb-4 flex items-center">
                    <span className="mr-3 text-4xl">🏆</span>
                    Master Instructor Journey
                  </h3>
                  <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                    <p>
                      From Dahab, Ahmed's journey brought him to Australia. Originally set to study mechanical engineering, his plans expanded when he reunited with the sea. He joined Abyss Scuba Diving for an internship and earned his Master Scuba Diver Trainer (MSDT) certification, officially turning his passion into a profession.
                    </p>
                    <p>
                      Now based in Sydney, Ahmed works full-time as a mechanical engineer while teaching diving part-time with Abyss Scuba Diving and guiding underwater adventures through his own business—sharing the underwater world he calls home. One of his favorite moments? Teaching a ten-year-old to dive and watching the same spark light up in the boy's eyes that once lit his own. "Anyone can dive," Ahmed says. "It's like the tree in Avatar—you don't just learn to breathe underwater; you connect to something bigger."
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-300">
                  <img
                    src="https://customer-assets.emergentagent.com/job_booking-integration/artifacts/apw7vtdf_1000054817.jpg"
                    alt="Ahmed - MSDT Certified Master Instructor"
                    className="w-full h-96 object-cover brightness-110 contrast-105"
                    style={{ objectPosition: 'center 20%' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10"></div>
                </div>
                <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-teal-500 to-teal-600 text-white p-6 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300">
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-1">MSDT</div>
                    <div className="text-sm font-medium">Master Instructor</div>
                    <div className="text-xs opacity-90 mt-1">Since 2016</div>
                  </div>
                </div>
              </div>
            </motion.div>
            </div>

            {/* Duplicate stats cards removed - already shown in header */}

            {/* Philosophy & Present Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-100 via-coral-50 to-teal-50 rounded-3xl p-8 lg:p-12 border border-blue-200"
            >
              <div className="text-center mb-8">
                <h3 className="text-4xl font-bold text-slate-800 mb-4 flex items-center justify-center">
                  <span className="mr-3">💫</span>
                  Living the Ocean Life
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                  <p>
                    Whether diving in Egypt's calm, colorful reefs or venturing into the deep blue off Australia's coast, Ahmed finds joy in the diversity of the ocean. He's shared the water with eagle rays, manta rays, and even once encountered a Southern Right Whale during a dive off Port Kembla—a moment that left him in complete awe.
                  </p>
                  <p>
                    He dreams of one day diving the Silfra fissure in Iceland—where you can literally swim between tectonic plates. But for now, he's exactly where he wants to be—sharing the ocean with others, one dive at a time.
                  </p>
                </div>
                <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                  <p>
                    His favorite marine creatures are Manta rays and eagle rays—creatures that mirror the grace and mystery of the sea itself.
                  </p>
                </div>
              </div>
              
              <div className="text-center">
                <div className="bg-white rounded-2xl p-6 inline-block shadow-lg">
                  <p className="text-2xl font-bold text-slate-800 mb-2">
                    "To Ahmed, the ocean isn't just a place."
                  </p>
                  <p className="text-xl text-coral-600 mb-2">It's a feeling. A way of life.</p>
                  <p className="text-lg text-slate-600">
                    And every time he takes a student underwater, he's not just teaching them to dive—he's helping them remember that connection.
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-gradient-to-br from-coral-100 via-blue-50 to-teal-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">My Adventure Philosophy</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Every adventure is carefully crafted to create meaningful experiences that go beyond traditional tourism
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {philosophy.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100"
              >
                <div className="flex items-start">
                  <div className="text-4xl mr-6 mt-2">{item.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Professional Qualifications</h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
              Continuously updating skills and certifications to provide the highest quality and safest experiences
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-3 sm:p-4 rounded-xl shadow-lg border border-slate-200 flex items-center"
              >
                <div className="text-2xl sm:text-3xl mr-3">{cert.icon}</div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm sm:text-base mb-0.5">{cert.title}</h3>
                  <p className="text-coral-600 font-medium text-xs sm:text-sm">Certified {cert.year}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Touch */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">Beyond the Adventures</h2>
              <div className="space-y-6 text-lg text-white/90 leading-relaxed">
                <p>
                  When I'm not guiding adventures, you'll find me exploring new dive sites along the NSW coast, 
                  working on ocean conservation projects, or perfecting my underwater photography skills. 
                  I believe that continuous learning and exploration make me a better guide and mentor.
                </p>
                <p>
                  My engineering background brings a unique approach to adventure planning—everything is meticulously 
                  planned for safety and efficiency, but with enough flexibility to embrace the spontaneous magic 
                  that nature provides.
                </p>
                <p>
                  I'm fluent in English and Arabic, and I love sharing stories about Australian marine life, 
                  indigenous water connections, and the incredible biodiversity of our coastal waters.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="relative overflow-hidden rounded-3xl shadow-2xl mb-6 hover:shadow-3xl transition-all duration-300">
                <img
                  src="https://customer-assets.emergentagent.com/job_booking-integration/artifacts/yee2wkww_1000054818.jpg"
                  alt="Ahmed - Beyond the adventures, ocean conservation advocate"
                  className="w-full h-96 object-cover brightness-115 contrast-110"
                  style={{ objectPosition: 'center 25%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/5"></div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
                <h3 className="text-xl font-bold mb-3">🌊 Ocean Conservation Advocate</h3>
                <p className="text-white/90">
                  Committed to protecting marine environments and educating adventurers about sustainable ocean practices
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-coral-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Let's Connect</h2>
          <p className="text-xl text-coral-100 mb-8">
            Ready to start your adventure? I'd love to hear about your goals and help you choose the perfect experience.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-coral-600 hover:bg-coral-50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Send a Message
            </Link>
            <a
              href="https://wa.me/61414710541"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              WhatsApp Chat
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;